import { type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Code2,
  Globe2,
  Moon,
  Package,
  Radar,
  Search,
  Server,
  ShieldCheck,
  Ship,
  Sun,
  Truck,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui';
import { cn } from '../lib/cn';
import { useTheme } from '../lib/theme';

/* ── kleine Bausteine ────────────────────────────────────────────────── */
function Pill({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
      {children}
    </span>
  );
}

function ProductCard({
  icon: Icon,
  title,
  desc,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">{desc}</p>
    </div>
  );
}

function Feature({
  img,
  alt,
  reverse,
  title,
  desc,
  bullets,
}: {
  img: string;
  alt: string;
  reverse?: boolean;
  title: string;
  desc: string;
  bullets: string[];
}): JSX.Element {
  return (
    <div className={cn('grid items-center gap-10 lg:grid-cols-2', reverse && 'lg:[direction:rtl]')}>
      <div className="lg:[direction:ltr]">
        <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">{title}</h3>
        <p className="mt-3 text-base leading-relaxed text-slate-700 dark:text-slate-300">{desc}</p>
        <ul className="mt-5 space-y-2.5">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
              <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-status-ok/15 text-status-ok">
                <Check className="h-3 w-3" />
              </span>
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:[direction:ltr]">
        <img
          src={img}
          alt={alt}
          loading="lazy"
          className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
        />
      </div>
    </div>
  );
}

export function LandingPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const { dark, setTheme } = useTheme();

  return (
    <div className="relative min-h-screen font-sans text-slate-900 dark:text-slate-100">
      {/* Container-Wand als durchgehender Marken-Hintergrund (wie im Portal). */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: "url('/hero-containers.webp')" }}
        aria-hidden
      />
      <div className="fixed inset-0 -z-10 bg-white/50 dark:bg-slate-950/75" aria-hidden />

      {/* ── Theme-Wähler (links): Besucher wählt Hell oder Dunkel ───── */}
      <div
        role="group"
        aria-label={t('landing.theme.label')}
        className="fixed bottom-6 left-4 z-40"
      >
        <div className="flex flex-col gap-1 rounded-full border border-slate-200 bg-white/90 p-1 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/90">
          <button
            type="button"
            onClick={() => setTheme('light')}
            aria-pressed={!dark}
            aria-label={t('landing.theme.light')}
            title={t('landing.theme.light')}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors',
              !dark
                ? 'bg-primary text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white',
            )}
          >
            <Sun className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setTheme('dark')}
            aria-pressed={dark}
            aria-label={t('landing.theme.dark')}
            title={t('landing.theme.dark')}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors',
              dark
                ? 'bg-primary text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white',
            )}
          >
            <Moon className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
        <div className="container flex h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img src="/brand-fclai-transparent.webp" alt="fclAI" className="h-12 w-auto sm:h-14" />
          </Link>
          <nav className="hidden items-center gap-7 md:flex">
            <a href="#products" className="text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">
              {t('landing.nav.product')}
            </a>
            <a href="#features" className="text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">
              {t('landing.nav.features')}
            </a>
            <a href="#how" className="text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">
              {t('landing.how.title')}
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-1 rounded-full border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800 sm:flex">
              {(['de', 'en'] as const).map((lng) => (
                <button
                  key={lng}
                  onClick={() => void i18n.changeLanguage(lng)}
                  className={cn(
                    'cursor-pointer rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors',
                    i18n.language === lng
                      ? 'bg-primary text-white'
                      : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white',
                  )}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </div>
            {token ? (
              <Link to="/dashboard">
                <Button>{t('landing.nav.toDashboard')}</Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost">{t('landing.nav.login')}</Button>
                </Link>
                <Link to="/login">
                  <Button>
                    {t('landing.nav.getStarted')}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-32 right-0 h-96 w-96 rounded-full bg-primary/15 blur-3xl" aria-hidden />
        <div className="container relative grid items-center gap-10 py-16 lg:grid-cols-2 lg:py-24">
          <div>
            <Pill>
              <span className="h-1.5 w-1.5 rounded-full bg-status-ok" />
              {t('landing.hero.badge')}
            </Pill>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 dark:text-white sm:text-5xl">
              {t('hero.headline')}
            </h1>
            <p className="mt-5 max-w-lg text-lg font-medium leading-relaxed text-slate-800 dark:text-slate-200">{t('hero.subline')}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/login">
                <Button size="default" className="h-11 px-6 text-base">
                  {t('landing.hero.ctaPrimary')}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="secondary" className="h-11 px-6 text-base">
                  {t('landing.hero.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <img
              src="/ContainerEarth.webp"
              alt={t('hero.headline')}
              className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
            />
          </div>
        </div>

        {/* Trust-Band */}
        <div className="border-y border-slate-200 bg-white/40 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/40">
          <div className="container flex flex-wrap items-center justify-center gap-3 py-5">
            <Pill>
              <Ship className="h-4 w-4 text-primary" /> {t('landing.trust.carriers')}
            </Pill>
            <Pill>
              <ShieldCheck className="h-4 w-4 text-primary" /> {t('landing.trust.eu')}
            </Pill>
            <Pill>
              <Server className="h-4 w-4 text-primary" /> {t('landing.trust.euBackup')}
            </Pill>
            <Pill>
              <Radar className="h-4 w-4 text-primary" /> {t('landing.trust.plans')}
            </Pill>
          </div>
        </div>
      </section>

      {/* ── Produkte ───────────────────────────────────────────────── */}
      <section id="products" className="container py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">{t('landing.products.title')}</h2>
          <p className="mt-3 text-slate-700 dark:text-slate-300">{t('landing.products.subtitle')}</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <ProductCard icon={Search} title={t('landing.products.lite.title')} desc={t('landing.products.lite.desc')} />
          <ProductCard icon={Radar} title={t('landing.products.premium.title')} desc={t('landing.products.premium.desc')} />
          <ProductCard icon={Code2} title={t('landing.products.api.title')} desc={t('landing.products.api.desc')} />
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────── */}
      <section id="features" className="bg-white/40 backdrop-blur-sm dark:bg-slate-900/40">
        <div className="container space-y-20 py-20">
          <Feature
            img="/Container_Status.webp"
            alt={t('landing.features.f1.title')}
            title={t('landing.features.f1.title')}
            desc={t('landing.features.f1.desc')}
            bullets={[t('landing.features.f1.b1'), t('landing.features.f1.b2'), t('landing.features.f1.b3')]}
          />
          <Feature
            img="/port.webp"
            alt={t('landing.features.f2.title')}
            reverse
            title={t('landing.features.f2.title')}
            desc={t('landing.features.f2.desc')}
            bullets={[t('landing.features.f2.b1'), t('landing.features.f2.b2'), t('landing.features.f2.b3')]}
          />
          <Feature
            img="/Security.webp"
            alt={t('landing.features.f3.title')}
            title={t('landing.features.f3.title')}
            desc={t('landing.features.f3.desc')}
            bullets={[t('landing.features.f3.b1'), t('landing.features.f3.b2'), t('landing.features.f3.b3')]}
          />
        </div>
      </section>

      {/* ── Dunkles Global-Band ────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <img
          src="/earth.webp"
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/70 to-slate-950/30" aria-hidden />
        <div className="container relative py-20 text-center">
          <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl">{t('landing.global.title')}</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/75">{t('landing.global.subline')}</p>
        </div>
      </section>

      {/* ── So funktioniert's ──────────────────────────────────────── */}
      <section id="how" className="container py-20">
        <h2 className="text-center text-3xl font-bold tracking-tight text-slate-900 dark:text-white">{t('landing.how.title')}</h2>
        <div className="mt-10 grid gap-8 md:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-lg font-bold text-white">
                {n}
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t(`landing.how.s${n}.title`)}</h3>
              <p className="mx-auto mt-2 max-w-xs text-base font-medium text-slate-800 dark:text-slate-200">{t(`landing.how.s${n}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Personas ───────────────────────────────────────────────── */}
      <section className="bg-white/40 backdrop-blur-sm dark:bg-slate-900/40">
        <div className="container py-20">
          <h2 className="text-center text-3xl font-bold tracking-tight text-slate-900 dark:text-white">{t('landing.personas.title')}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { icon: Truck, key: 'forwarders' },
              { icon: Ship, key: 'carriers' },
              { icon: Package, key: 'importers' },
            ].map(({ icon: Icon, key }) => (
              <div key={key} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t(`landing.personas.${key}.title`)}</h3>
                <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{t(`landing.personas.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA-Band ───────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <img
          src="/BlueBanner.webp"
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" aria-hidden />
        <div className="container relative py-20">
          <div className="max-w-xl">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('landing.cta.title')}</h2>
            <p className="mt-3 text-white/80">{t('landing.cta.subline')}</p>
            <Link to="/login" className="mt-7 inline-block">
              <Button className="h-11 px-6 text-base">
                {t('landing.cta.button')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
        <div className="container grid gap-8 py-12 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <img src="/brand-fclai.webp" alt="fclAI" className="h-9 w-auto" />
            <p className="mt-3 max-w-xs text-sm text-slate-500 dark:text-slate-400">{t('landing.footer.tagline')}</p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{t('landing.footer.product')}</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-300">
              <li><a href="#products" className="hover:text-slate-900 dark:hover:text-white">{t('landing.nav.product')}</a></li>
              <li><a href="#features" className="hover:text-slate-900 dark:hover:text-white">{t('landing.nav.features')}</a></li>
              <li><Link to="/login" className="hover:text-slate-900 dark:hover:text-white">{t('landing.nav.login')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{t('landing.footer.legal')}</h4>
            <ul className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-300">
              <li><a href="https://structureai.de" target="_blank" rel="noreferrer" className="hover:text-slate-900 dark:hover:text-white">{t('landing.footer.privacy')}</a></li>
              <li><a href="https://structureai.de" target="_blank" rel="noreferrer" className="hover:text-slate-900 dark:hover:text-white">{t('landing.footer.imprint')}</a></li>
              <li><a href="https://structureai.de" target="_blank" rel="noreferrer" className="hover:text-slate-900 dark:hover:text-white">{t('landing.footer.terms')}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800">
          <div className="container flex flex-col items-center justify-between gap-3 py-5 text-sm text-slate-500 dark:text-slate-400 sm:flex-row">
            <span>© 2026 StructureAI GmbH · {t('landing.footer.rights')}</span>
            <div className="flex items-center gap-3">
              <a
                href="https://www.structureai.de"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 font-medium text-slate-700 transition-colors hover:text-slate-900 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-white"
              >
                {t('landing.footer.poweredBy')}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
              <span className="flex items-center gap-1.5">
                <Globe2 className="h-4 w-4" /> EU
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
